import { useState } from 'react';
import { supabase } from '../supabase';
import type { DodCriterion } from '../../components/projects/types';

export interface ProjectPatch {
  name: string;
  description: string;
  /** The expanded, formatted project overview (markdown). Additive beside the
   * short one-line `description` goal — patched independently, so editing one
   * never overwrites the other. */
  overview: string | null;
  status: string;
  lead_id: string | null;
  target_date: string | null;
  definition_of_done: DodCriterion[];
}

export interface UseSaveProject {
  saving: boolean;
  error: string | null;
  /**
   * Persists a partial patch — only the provided keys are written, so callers
   * can save a single field (`{ status }`) for inline autosave or a full row.
   * Returns true on success, false on failure (error is set).
   */
  save: (orgId: string, projectId: string, patch: Partial<ProjectPatch>) => Promise<boolean>;
}

export function useSaveProject(): UseSaveProject {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (orgId: string, projectId: string, patch: Partial<ProjectPatch>): Promise<boolean> => {
    setSaving(true);
    setError(null);
    try {
      // Build the update from only the keys the caller supplied — an undefined
      // field is left untouched rather than nulled.
      const update: Record<string, unknown> = {};
      if (patch.name !== undefined) update.name = patch.name;
      if (patch.description !== undefined) update.description = patch.description;
      if (patch.overview !== undefined) update.overview = patch.overview;
      if (patch.status !== undefined) update.status = patch.status;
      if (patch.lead_id !== undefined) update.lead_id = patch.lead_id;
      if (patch.target_date !== undefined) update.target_date = patch.target_date;
      if (patch.definition_of_done !== undefined) update.definition_of_done = patch.definition_of_done;

      const { error: upErr } = await supabase
        .from('projects')
        .update(update)
        .eq('id', projectId)
        .eq('org_id', orgId);
      if (upErr) {
        setError(upErr.message);
        return false;
      }
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unexpected error saving project');
      return false;
    } finally {
      setSaving(false);
    }
  };

  return { saving, error, save };
}
